import { ApplicationRef, EventEmitter, Injectable } from '@angular/core';
import Venue from '../../../models/Venue';
import firebase from 'firebase/app';
import 'firebase/auth';
import Order from '../../../models/Order';
import Session from '../../../models/Session';
import Payment from '../../../models/Payment';
import Customer from '../../../models/Customer';
import { AngularFireAuth } from '@angular/fire/auth';
import Address from '../../../models/Address';
import { Api, sanitizeId } from '../../../api/api';
import Utils from '../../../utils';
import { PreorderType } from '../../../enums/PreorderType';
import { OrderType } from '../../../enums/OrderType';
import Preorder from '../../../models/Preorder';
import { environment } from '../../../environments/environment';
import OptionGroup from '../../../models/OptionGroup';
import Article from '../../../models/Article';
import * as moment from 'moment';
import { OrderUtils } from '../../../utils/order-utils';
import { TimeUtils } from '../../../utils/time-utils';
import { ModalController } from '@ionic/angular';
import Auth = firebase.auth.Auth;
import User = firebase.User;
import { TranslateService } from '@ngx-translate/core';
import { PaymentModalComponent, PaymentModalResult } from '../../components/payment-modal/payment-modal.component';
import { DisplayIdentifier } from '../../enums/DisplayIdentifier';
import { AngularFireAnalytics } from '@angular/fire/analytics';
import { AnalyticsUtils } from '../../../utils/analytics-utils';

const localStorageOrderKey = 'repository-service-order';
const localStorageVerifiedOrderKey = 'repository-service-verified-order';
const localStorageCustomerKey = 'repository-service-customer';
const localStoragePaymentKey = 'repository-service-payment';
const localStorageAddressKey = 'repository-service-address';
const localStorageInformationsKey = 'repository-service-informations';
const localStoragePreviousPreorderKey = 'repository-service-previous-preorder';

@Injectable({
	providedIn: 'root',
})
export class RepositoryService {
	venue = new EventEmitter<Venue>();
	address = new EventEmitter<Address>();
	user = new EventEmitter<User>();
	order = new EventEmitter<Order>();
	verifiedOrder = new EventEmitter<Order>();
	payment = new EventEmitter<Payment>();
	customer = new EventEmitter<Customer>();
	previousPreorder = new EventEmitter<Preorder>();

	_venue: Venue;
	_address: Address;
	_order: Order;
	_verifiedOrder: Order;
	_payment: Payment;
	_customer: Customer;
	_user: User;
	_previousPreorder: Preorder;

	private venuePromise: Promise<Venue> = null;
	private venuePromiseRunning: string = null;

	constructor(private auth: AngularFireAuth, private appRef: ApplicationRef, private analytics: AngularFireAnalytics) {
		this.user.subscribe(async user => {
			this._user = user;
			await this.reloadCustomer(user);
			appRef.tick();
		});
		auth.auth
			.setPersistence(Auth.Persistence.LOCAL)
			.then(() => {
				auth.auth.onAuthStateChanged(user => {
					if (user === null) {
						auth.auth.signInAnonymously();
					} else {
						this.user.emit(user);
					}
				});
			})
			.catch(err => console.error(err));

		this._venue = null;
		this._order = JSON.parse(localStorage.getItem(localStorageOrderKey));
		this._payment = JSON.parse(localStorage.getItem(localStoragePaymentKey));
		this._customer = JSON.parse(localStorage.getItem(localStorageCustomerKey));
		this._address = JSON.parse(localStorage.getItem(localStorageAddressKey));
		this._verifiedOrder = JSON.parse(localStorage.getItem(localStorageVerifiedOrderKey));
		this._previousPreorder = JSON.parse(localStorage.getItem(localStoragePreviousPreorderKey));

		this.order.subscribe(order => {
			if (order) {
				OrderUtils.injectRequiredArticles(this._venue, order, analytics);
				OrderUtils.injectDeliveryFees(this._venue, order);
				order.orderedArticles = order.orderedArticles.map(articleGroup => {
					articleGroup.groups = articleGroup.groups.sort((option1, option2) => {
						if (option1.article.sortOrder > option2.article.sortOrder) {
							return 1;
						}
						if (option1.article.sortOrder < option2.article.sortOrder) {
							return -1;
						}
						return 0;
					});
					return articleGroup;
				});
			}
			this._order = order;
			localStorage.setItem(localStorageOrderKey, JSON.stringify(order));
			appRef.tick();
		});
		this.verifiedOrder.subscribe(verifiedOrder => {
			this._verifiedOrder = verifiedOrder;
			localStorage.setItem(localStorageVerifiedOrderKey, JSON.stringify(verifiedOrder));
			appRef.tick();
		});
		this.address.subscribe(address => {
			this._address = address;
			localStorage.setItem(localStorageAddressKey, JSON.stringify(address));
			appRef.tick();
		});
		this.payment.subscribe(payment => {
			this._payment = payment;
			localStorage.setItem(localStoragePaymentKey, JSON.stringify(payment));
			appRef.tick();
		});
		this.customer.subscribe(customer => {
			this._customer = customer;
			localStorage.setItem(localStorageCustomerKey, JSON.stringify(customer));
			appRef.tick();
		});
		this.venue.subscribe(venue => {
			this._venue = venue;
			appRef.tick();
		});
		this.previousPreorder.subscribe(preorder => {
			this._previousPreorder = preorder;
			localStorage.setItem(localStoragePreviousPreorderKey, JSON.stringify(preorder));
		});
		this.address.emit(this._address);
		this.order.emit(this._order);
		this.verifiedOrder.emit(this._verifiedOrder);
		this.payment.emit(this._payment);
		this.customer.emit(this._customer);
	}

	async reloadCustomer(user: firebase.User) {
		if (!user || user.isAnonymous) {
			this.sanitizeAndEmitCustomer(user, null);
			return;
		}
		let customer: Customer;
		try {
			const response = await Api.getCustomer(user.uid);
			customer = response.data;
			const userUidTokens = customer.userUid.split('_');
			customer.userUid = userUidTokens[userUidTokens.length - 1];
			const splitPhone = Utils.phoneToPhoneCountryAndPhone(customer.phone);
			if (splitPhone) {
				customer.phoneCountry = splitPhone.phoneCountry;
				customer.phone = splitPhone.phone;
			} else {
				customer.phoneCountry = null;
				customer.phone = '';
			}
		} catch (e) {
			console.error(e);
			customer = null;
		}
		this.sanitizeAndEmitCustomer(user, customer);
	}

	resetVenue() {
		this._venue = null;
		this.venue.emit(this._venue);
	}

	getVenue(forcedId: string | null = null): Promise<Venue> {
		let venueId = forcedId;
		if (venueId === null) {
			venueId = this.getStoredVenueId();
		}
		if (venueId === null || venueId.length <= 0) {
			if (this._venue) {
				return new Promise<Venue>(resolve => resolve(this._venue));
			} else {
				return new Promise<Venue>((resolve, reject) => reject('No Venue ID Provided'));
			}
		}
		if (this._venue && venueId === this._venue._id) {
			this.venue.emit(this._venue);
			return new Promise<Venue>(resolve => resolve(this._venue));
		}
		if (this.venuePromiseRunning === venueId) {
			return this.venuePromise;
		}
		this.venuePromiseRunning = venueId;
		this.venuePromise = new Promise<Venue>(async (resolve, reject) => {
			try {
				const venue = (await Api.getLazyVenue(venueId)).data;
				venue.openingHours = TimeUtils.sanitizeHours(venue.openingHours);
				venue.deliveryHours = TimeUtils.sanitizeHours(venue.deliveryHours);
				venue.articleCategories = (await Api.getCategoriesWithArticles(venue._id, true)).data;
				const optionGroups: OptionGroup[] = (await Api.getOptionGroupsByVenue(venue._id)).data;
				const allArticles = [];
				venue.articleCategories.forEach(cat => {
					allArticles.push(...cat.articles);
				});
				venue.articleCategories = venue.articleCategories
					.map(category => {
						// filter inactive categories
						category.articles = category.articles
							.map(article => {
								// filter inactive groups (empty options)
								article.groups = article.groups
									.map(groupId => {
										// @ts-ignore
										const group = optionGroups.find(grp => grp._id === groupId);
										// filter inactive options
										group.articles = group.articles
											.map(optionArticle => this.sanitizeArticle(venue, optionArticle))
											.sort((opt1, opt2) => {
												if (opt1.sortOrder === opt2.sortOrder) {
													return 0;
												}
												if (opt1.sortOrder > opt2.sortOrder) {
													return 1;
												}
												return -1;
											});
										return group;
									})
									.filter(group => group.articles.length > 0);
								article = this.sanitizeArticle(venue, article);
								return article;
							})
							.sort((art1, art2) => {
								if (art1.sortOrder === art2.sortOrder) {
									return 0;
								}
								if (art1.sortOrder > art2.sortOrder) {
									return 1;
								}
								return -1;
							});
						return category;
					})
					.map(category => {
						category.hidden = !(
							category.visible &&
							category.articles.length > 0 &&
							(!category.availableHours ||
								category.availableHours.length === 0 ||
								TimeUtils.doesHoursMatchNow(category.availableHours))
						);
						return category;
					})
					.sort((cat1, cat2) => {
						if (cat1.sortOrder === cat2.sortOrder) {
							return 0;
						}
						if (cat1.sortOrder > cat2.sortOrder) {
							return 1;
						}
						return -1;
					});
				this._venue = venue;
				this.venue.emit(this._venue);
				resolve(this._venue);
			} catch (e) {
				this.venue.emit(null);
				this.venue.error(e);
				reject(e);
			}
		}).finally(() => {
			this.venuePromiseRunning = null;
		});
		return this.venuePromise;
	}

	createOrder(venue: Venue, preorderType: PreorderType) {
		let order: Order;
		if (this._order == null || this._order.venue !== venue._id) {
			order = new Order();
			order.orderedArticles = [];
			order.venue = venue._id;
		} else {
			order = this._order;
			order._id = undefined;
			order.session = undefined;
			order.table = undefined;
		}
		order.currency = venue.currency;
		order.type = OrderType.PREORDER;
		order.preorder = new Preorder();
		order.preorder.number = '';
		order.preorder.type = preorderType;
		if (preorderType === PreorderType.TAKE_AWAY) {
			order.preorder.street = venue.street;
			order.preorder.city = venue.city.de;
			order.preorder.number = venue.number;
			order.preorder.postalCode = venue.postalCode;
		}
		order.preorder.personCount = 1;
		this.order.emit(order);
	}

	async sendOrderAndPay(modalCtrl: ModalController, order: Order, analytics: AngularFireAnalytics): Promise<any> {
		this.order.emit(order);
		return new Promise<any>(async (resolve, reject) => {
			const orderCopy: Order = JSON.parse(JSON.stringify(this._order));
			orderCopy.preorder.phone = orderCopy.preorder.phoneCountry.tel.substr(1) + order.preorder.phone;
			const response = await Api.createOrder(orderCopy);
			AnalyticsUtils.checkoutProgress(order, null, 2, analytics);
			const venue = await this.getVenue();
			const verifiedOrder = response.data;
			const payment = Utils.paymentFromOrder(verifiedOrder);
			const result = await PaymentModalComponent.show(modalCtrl, venue, payment, order);
			if (result && result.result === PaymentModalResult.SUCCESS) {
				this.verifiedOrder.emit(verifiedOrder);
				resolve();
				return;
			}
			reject(result && result.error ? result.error : null);
			return;
		});
	}

	async createSession(): Promise<Session> {
		return new Promise<Session>((resolve, reject) => {
			Api.createSession({
				locale: 'de',
				deviceType: 'web',
				table: this._venue.preorderTable,
				userUid: this._user.uid,
				deviceToken: 'empty',
			})
				.then(session => {
					resolve(session.data);
				})
				.catch(err => {
					console.error(err);
					reject(err);
				});
		});
	}

	getStoredVenueId(): string {
		return this._order ? this._order.venue : null;
	}

	async reorder(translate: TranslateService, order: Order) {
		let someArticleNotAvailable = false;
		const localOrder: Order = JSON.parse(JSON.stringify(order));
		const venue = await this.getVenue(localOrder.venue);
		if (!Utils.venueAcceptsOrders(venue, order.preorder.type)) {
			throw {
				message: translate.instant('errors.venue_not_open'),
			};
		}
		const articleList = [];
		for (const category of venue.articleCategories) {
			for (const article of category.articles) {
				articleList.push(article);
				for (const group of article.groups) {
					for (const optionArticle of group.articles) {
						articleList.push(optionArticle);
					}
				}
			}
		}
		localOrder.orderedArticles = localOrder.orderedArticles.map(articleGroup => {
			articleGroup.article = articleList.find(article => articleGroup.article._id === article._id);
			if (articleGroup.article === undefined) {
				someArticleNotAvailable = true;
			}
			articleGroup.groups = articleGroup.groups.map(articleOption => {
				articleOption.article = articleList.find(article => articleOption.article._id === article._id);
				if (articleOption.article === undefined) {
					someArticleNotAvailable = true;
				}
				return articleOption;
			});
			return articleGroup;
		});
		localOrder.session = undefined;
		localOrder.table = undefined;
		localOrder.orderAt = null;
		localOrder._id = null;
		if (someArticleNotAvailable) {
			throw {
				message: translate.instant('errors.reorder_products_not_available'),
			};
		}
		this.order.emit(localOrder);
	}

	async getVenuesByAddress(address: Address): Promise<Venue[]> {
		const venues: Venue[] = [];
		if (address.postalCode) {
			venues.push(...(await this.getVenues(null, null, address.postalCode))
				.map(ven => {
					ven.isPostalDelivery = true;
					return ven;
				})
				.filter(ven => {
					return Utils.venueAcceptsOrders(ven, PreorderType.DELIVERY);
				})
			);
		}
		const ids = venues.map(ven => ven._id);
		(await this.getVenues(address.lat, address.lng, null))
			.filter(ven => {
				return Utils.venueAcceptsOrders(ven, PreorderType.DELIVERY) || Utils.venueAcceptsOrders(ven, PreorderType.TAKE_AWAY);
			})
			.forEach(ven => {
				if (ids.indexOf(ven._id) < 0) {
					venues.push(ven);
				}
			});
		return venues.filter(ven => ven.customerGroup === environment.customerGroup);
	}

	async getVenues(lat: number | null, lng: number | null, postalCode: string | null): Promise<Venue[]> {
		const response = postalCode ? await Api.getVenuesBy(postalCode) : await Api.getVenues(lat, lng, 50, false);
		return response.data
			.map(venue => {
				venue.openingHours = TimeUtils.sanitizeHours(venue.openingHours);
				venue.deliveryHours = TimeUtils.sanitizeHours(venue.deliveryHours);
				venue.isPostalDelivery = !!postalCode;
				if (venue.deliveryByRadius === undefined) {
					venue.deliveryByRadius = false;
				}
				return venue;
			})
			.filter(async venue => {
				try {
					if (venue.customerGroup !== environment.customerGroup) {
						return false;
					}
					if (venue.panicEndAt && moment(venue.panicEndAt).isSameOrAfter(moment())) {
						return false;
					}
					return (
						(await Utils.getSlots(venue, PreorderType.DELIVERY)).length !== 0 ||
						(await Utils.getSlots(venue, PreorderType.TAKE_AWAY)).length !== 0
					);
				} finally {
				}
				return false;
			});
	}

	sanitizeAndEmitCustomer(user: firebase.User, customer: Customer) {
		if (customer == null) {
			customer = new Customer();
			customer.userUid = user ? sanitizeId(user.uid) : null;
		}
		customer.email = user == null || user.isAnonymous ? customer.email : user.email;
		if (!customer.phoneCountry && customer.phone) {
			const res = Utils.phoneToPhoneCountryAndPhone(customer.phone);
			customer.phoneCountry = res.phoneCountry;
			customer.phone = res.phone;
		}
		this._customer = customer;
		this.customer.emit(customer);
	}

	async getAllVenues(): Promise<Venue[]> {
		const response = await Api.getAllVenues(false);
		return response.data
			.map(venue => {
				venue.openingHours = TimeUtils.sanitizeHours(venue.openingHours);
				venue.deliveryHours = TimeUtils.sanitizeHours(venue.deliveryHours);
				if (venue.deliveryByRadius === undefined) {
					venue.deliveryByRadius = false;
				}
				return venue;
			})
			.filter(venue => {
				if (venue.customerGroup !== environment.customerGroup) {
					return false;
				}
				return !(venue.panicEndAt && moment(venue.panicEndAt).isSameOrAfter(moment()));
			});
	}

	private sanitizeArticle(venue: Venue, article: Article): Article {
		if (article && article.groups) {
			article.isFromPrice = article.groups.map(grp =>
				grp.displayIdentifiers && grp.displayIdentifiers.indexOf(DisplayIdentifier.fullPrice) >= 0
			).indexOf(true) >= 0;
		}
		article.availableHours = TimeUtils.sanitizeHours(article.availableHours);
		article.hidden = !(
			article.visible &&
			article.isActive &&
			(!article.availableHours || article.availableHours.length === 0 || TimeUtils.doesHoursMatchNow(article.availableHours))
		);
		if (article.displayPrice) {
			article.price = article.displayPrice ? article.displayPrice : article.price;
		}
		if (article.displayPriceByType) {
			const byType = article.displayPriceByType;
			if (byType.standard) {
				article.priceByType.standard = byType.standard;
			}
			if (byType.preorder.delivery) {
				article.priceByType.preorder.delivery = byType.preorder.delivery;
			}
			if (byType.preorder.inside) {
				article.priceByType.preorder.inside = byType.preorder.inside;
			}
			if (byType.preorder.takeAway) {
				article.priceByType.preorder.takeAway = byType.preorder.takeAway;
			}
			if (byType.terminal.inside) {
				article.priceByType.terminal.inside = byType.terminal.inside;
			}
			if (byType.terminal.takeAway) {
				article.priceByType.terminal.takeAway = byType.terminal.takeAway;
			}
		}
		article.tags = article.tags
			.filter(tag => tag !== undefined && tag !== null)
			.map(tag => {
				if (tag._id === undefined) {
					// @ts-ignore
					return venue.tags.find(srcTag => tag === srcTag._id);
				}
				return tag;
			});
		return article;
	}

	getShownInformations(): string[] {
		const informations: string[] = JSON.parse(localStorage.getItem(localStorageInformationsKey));
		return !informations || !informations.length ? [] : informations;
	}

	addShownInformations(id: string) {
		const informations = this.getShownInformations();
		informations.push(id);
		localStorage.setItem(localStorageInformationsKey, JSON.stringify(informations));
	}
}
